import React, { useCallback, useEffect, useState } from 'react';

// Time off for one person (People > Edit Person > Availability, and My Portal > Availability): whole days
// (3 Nov, or 3 to 5 Nov) or part of a day (3 Nov 9am to 5pm). Nobody can be rostered inside their time off.
// The caller supplies `api`: load() -> [{ id, startDate, endDate, startTime, endTime, allDay, note }],
// add({ startDate, endDate?, startTime?, endTime?, note? }), remove(id).
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const longDate = (s) => new Date(`${s}T00:00:00`).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
const clock = (hhmm) => {
  const [h, m] = String(hhmm).split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'pm' : 'am'}`;
};

// A shift's start. Stored times are wall-clock values sent as UTC, so read them as UTC.
const shiftWhen = (iso) => {
  const d = new Date(iso);
  const dateText = d.toLocaleDateString('en-AU', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' });
  if (d.getUTCHours() === 0 && d.getUTCMinutes() === 0) return dateText;
  return `${dateText}, ${clock(`${d.getUTCHours()}:${d.getUTCMinutes()}`)}`;
};
const KIND_WORD = { game: 'Game', coach: 'Coaching', court: 'Court' };

function describe(item) {
  if (!item.allDay) return `${longDate(item.startDate)}, ${clock(item.startTime)} to ${clock(item.endTime)}`;
  if (item.endDate === item.startDate) return `${longDate(item.startDate)} (all day)`;
  return `${longDate(item.startDate)} to ${longDate(item.endDate)}`;
}

export default function PersonTimeOff({ api, readOnly = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState('days'); // 'days' | 'period'
  const [form, setForm] = useState({ startDate: '', endDate: '', startTime: '09:00', endTime: '17:00', note: '' });
  // Already rostered in that period: ask first (pending), then say what happened (notice).
  const [pending, setPending] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    try {
      setItems(await api.load());
      setError('');
    } catch (err) {
      setError(err.message || 'Could not load time off');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  async function submit() {
    setError('');
    if (!form.startDate) { setError('Choose the date of your time off'); return; }
    const body = mode === 'period'
      ? { startDate: form.startDate, startTime: form.startTime, endTime: form.endTime, note: form.note }
      : { startDate: form.startDate, endDate: form.endDate || form.startDate, note: form.note };
    await save(body);
  }

  // Sends the time off. The server answers needsConfirmation when the person is already rostered then.
  async function save(body) {
    setBusy(true);
    setNotice(null);
    try {
      const result = await api.add(body);
      if (result && result.needsConfirmation) {
        setPending({ body, clashes: result.clashes || [] });
        return;
      }
      setPending(null);
      setForm((f) => ({ ...f, startDate: '', endDate: '', note: '' }));
      if (result && ((result.rejected || []).length || (result.couldNot || []).length)) setNotice(result);
      await load();
    } catch (err) {
      setError(err.message || 'Could not save');
    } finally {
      setBusy(false);
    }
  }

  async function remove(id) {
    setBusy(true);
    try {
      await api.remove(id);
      await load();
    } catch (err) {
      setError(err.message || 'Could not remove');
    } finally {
      setBusy(false);
    }
  }

  const set = (changes) => setForm((f) => ({ ...f, ...changes }));

  return (
    <div style={{ marginTop: '1.5rem' }}>
      <h4 style={{ margin: '0 0 0.25rem' }}>Time Off</h4>
      <p style={{ margin: '0 0 0.6rem', color: '#666', fontSize: '0.8rem' }}>
        Days off (one day, or a run of days) or a period within a day. Nobody can be selected for a game, session or shift in that time. Saved as you add or remove it.
      </p>
      {error && <p style={{ color: '#d32f2f', margin: '0 0 0.6rem' }} role="alert">{error}</p>}
      {notice && (
        <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 4, padding: '0.6rem 0.8rem', margin: '0 0 0.75rem', fontSize: '0.9rem' }}>
          <strong>Time off added.</strong>
          {notice.rejected.length > 0 && <> {notice.rejected.length} rostered shift{notice.rejected.length === 1 ? ' was' : 's were'} rejected so they can be replaced.</>}
          {notice.couldNot.length > 0 && (
            <div style={{ marginTop: 6, color: '#8a4b00' }}>
              These could not be rejected automatically, so please contact the administrator about them:
              <ul style={{ margin: '4px 0 0 1.1rem' }}>
                {notice.couldNot.map((c, i) => <li key={i}>{KIND_WORD[c.kind]}: {c.title} ({shiftWhen(c.startsAt)}), because {c.blockedReason}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
      {pending && (
        <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 4, padding: '0.75rem 0.9rem', margin: '0 0 0.75rem', fontSize: '0.9rem' }} role="alertdialog">
          <strong>Already rostered in this time</strong>
          <ul style={{ margin: '6px 0', paddingLeft: '1.1rem' }}>
            {pending.clashes.map((c, i) => (
              <li key={i}>{KIND_WORD[c.kind]}: {c.title} ({c.role}), {shiftWhen(c.startsAt)}
                {c.canReject ? '' : <span style={{ color: '#8a4b00' }}> — cannot be rejected automatically ({c.blockedReason}); the administrator will need to deal with it</span>}
              </li>
            ))}
          </ul>
          {pending.clashes.some((c) => c.canReject) && (
            <p style={{ margin: '0 0 8px' }}>Adding this time off will <strong>reject</strong> {pending.clashes.filter((c) => c.canReject).length === pending.clashes.length ? 'these shifts' : 'the shifts that can be rejected'}, so they can be replaced. A record of it is kept.</p>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn-submit" disabled={busy} style={{ padding: '0.45rem 1rem' }} onClick={() => save({ ...pending.body, confirm: true })}>
              {pending.clashes.some((c) => c.canReject) ? 'Add time off and reject' : 'Add time off anyway'}
            </button>
            <button type="button" className="btn-cancel" disabled={busy} onClick={() => setPending(null)}>Cancel</button>
          </div>
        </div>
      )}
      {loading ? <p style={{ color: '#666' }}>Loading time off...</p> : (
        <div style={{ marginBottom: '0.75rem' }}>
          {items.length === 0 ? (
            <p style={{ color: '#666', margin: 0 }}>No time off booked</p>
          ) : (
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {items.map((item) => (
                <div key={item.id} style={{ backgroundColor: '#e3f2fd', padding: '0.5rem 1rem', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span>{describe(item)}{item.note ? ` · ${item.note}` : ''}</span>
                  {!readOnly && (
                    <button type="button" onClick={() => remove(item.id)} disabled={busy} aria-label="Remove time off"
                      style={{ background: 'none', border: 'none', color: '#d32f2f', cursor: 'pointer', fontSize: '16px' }}>✕</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {!readOnly && (
        <div style={{ display: 'grid', gap: '0.5rem' }}>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <input type="radio" name="timeoff-mode" checked={mode === 'days'} onChange={() => setMode('days')} /> Day(s) off
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <input type="radio" name="timeoff-mode" checked={mode === 'period'} onChange={() => setMode('period')} /> Part of a day
            </label>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <label>{mode === 'days' ? 'From ' : 'Date '}
              <input type="date" min={todayStr()} value={form.startDate} onChange={(e) => set({ startDate: e.target.value, endDate: form.endDate && form.endDate < e.target.value ? '' : form.endDate })} />
            </label>
            {mode === 'days' ? (
              <label>To (optional){' '}
                <input type="date" min={form.startDate || todayStr()} value={form.endDate} onChange={(e) => set({ endDate: e.target.value })} />
              </label>
            ) : (
              <>
                <label>From <input type="time" value={form.startTime} onChange={(e) => set({ startTime: e.target.value })} /></label>
                <label>To <input type="time" value={form.endTime} onChange={(e) => set({ endTime: e.target.value })} /></label>
              </>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input type="text" maxLength={200} placeholder="Note (optional)" value={form.note} onChange={(e) => set({ note: e.target.value })} style={{ flex: 1 }} />
            <button type="button" onClick={submit} disabled={busy} className="btn-submit" style={{ padding: '0.5rem 1rem' }}>Add time off</button>
          </div>
        </div>
      )}
    </div>
  );
}
